// src/controllers/egresoCabController.js

import {
  getEgresoCabs,
  getEgresoCabsWithExpandedData,
  getEgresoCabsForPicker,
  getEgresoCabsForPickerBoard,
  claimEgresoForPicker,
  createEgresoCab,
  updateEgresoCab,
  deleteEgresoCab,
} from '../models/egresoCabModel.js';
import { getEgresoDets } from '../models/egresoDetModel.js';
import Swal from 'sweetalert2';
import pb from '../lib/pocketbase.js';
import { toasts } from "svelte-toasts";
import { subscribeSafe, removeSubscription } from '../lib/realtime.js';

let previousValues = new Map(); // Almacena los valores anteriores de los registros

// Utilidad para evitar demasiados toasts (throttle/deduplicación)
const toastHistory = new Map();
function showToastWithThrottle({ title, description, ...options }) {
  const key = title + description;
  const now = Date.now();
  const THROTTLE_TIME = 3000; // 3 segundos
  if (now - (toastHistory.get(key) || 0) > THROTTLE_TIME) {
    toasts.add({ title, description, ...options });
    toastHistory.set(key, now);
  }
}

// Obtener lista de egresos paginados SIN los detalles de egresoDet
export async function fetchEgresoCabs(page, perPage) {
  try {
    const response = await getEgresoCabs(page, perPage);

    return {
      egresoCab: response.items, // Solo devolvemos la lista de egresos sin detalles
      totalRecords: response.totalItems,
      currentPage: response.page,
    };
  } catch (error) {
    console.error("Error fetching egresoCabs:", error);
    Swal.fire('Error', 'No se pudieron cargar los egresos', 'error');
    return null;
  }
}

// Obtener lista de egresos con datos expandidos (picker, escolta, corredor)
export async function fetchEgresoCabsWithExpandedData(filters = {}) {
  try {
    const response = await getEgresoCabsWithExpandedData(filters);

    return {
      egresoCab: response.items,
      totalRecords: response.totalItems,
      currentPage: response.page,
    };
  } catch (error) {
    console.error("Error fetching egresoCabs with expanded data:", error);
    Swal.fire('Error', 'No se pudieron cargar los egresos', 'error');
    return null;
  }
}

// Obtener egresos asignados al picker autenticado
export async function fetchEgresoCabsForPicker(userId) {
  try {
    const response = await getEgresoCabsForPicker(userId);
    return {
      egresoCab: response.items,
      totalRecords: response.totalItems,
      currentPage: response.page,
    };
  } catch (error) {
    console.error('Error fetching picker egresoCabs:', error);
    Swal.fire('Error', 'No se pudieron cargar tus pedidos asignados', 'error');
    return null;
  }
}

/** Tablero mesa: Autorizado por créditos + Asignación de mesa (míos). */
export async function fetchEgresoCabsForPickerBoard(userId, filters = {}) {
  try {
    return await getEgresoCabsForPickerBoard(userId, filters);
  } catch (error) {
    console.error('Error fetching picker board:', error);
    Swal.fire('Error', 'No se pudo cargar el tablero de mesa', 'error');
    return null;
  }
}

/** Mesa toma un pedido autorizado. */
export async function claimEgresoCabForPicker(egresoId, userId) {
  try {
    await claimEgresoForPicker(egresoId, userId);
    showToastWithThrottle({
      title: 'Pedido tomado',
      description: 'Quedó en Asignación de mesa',
      duration: 3000,
      placement: 'bottom-right',
      type: 'success',
      theme: 'dark',
      showProgress: true,
    });
    return { success: true };
  } catch (error) {
    console.error('Error claiming egreso for picker:', error);
    const msg =
      error?.code === 'ALREADY_CLAIMED'
        ? 'Ese pedido ya lo tomó otra mesa'
        : 'No se pudo tomar el pedido';
    showToastWithThrottle({
      title: 'Error',
      description: msg,
      duration: 4000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true,
    });
    return { success: false, error };
  }
}

// Guardar o actualizar un egreso
export async function saveEgresoCab(egresoCab, currentEgresoCabId) {
  try {
    if (currentEgresoCabId) {
      await updateEgresoCab(currentEgresoCabId, egresoCab);
      showToastWithThrottle({
        title: 'Éxito',
        description: 'Egreso actualizado exitosamente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true
      });
    } else {
      await createEgresoCab(egresoCab);
      showToastWithThrottle({
        title: 'Éxito',
        description: 'Egreso creado exitosamente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true
      });
    }
  } catch (error) {
    console.error("Error saving egresoCab:", error);
    showToastWithThrottle({
      title: 'Error',
      description: 'Ocurrió un error al guardar el egreso',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true
    });
  }
}

// Eliminar un egreso
export async function removeEgresoCab(id) {
  try {
    await deleteEgresoCab(id);
    showToastWithThrottle({
      title: 'Éxito',
      description: 'Egreso eliminado exitosamente.',
      duration: 3000,
      placement: 'bottom-right',
      type: 'success',
      theme: 'dark',
      showProgress: true
    });
  } catch (error) {
    console.error("Error deleting egresoCab:", error);
    Swal.fire('Error', 'No se pudo eliminar el egreso', 'error');
  }
}

// Mapeo de estados a tipos de toast
const statusToToastType = {
  NOT_READY: 'error',
  PACKING: 'warning',
  PARTIALLY_READY: 'warning',
  READY: 'info',
  ON_ROUTE: 'info',
  DELIVERED: 'success',
  RETURNED: 'error',
  DEFAULT: 'info'
};

// Obtiene un mensaje descriptivo según el estado
function getStatusMessage(status) {
  const messages = {
    NOT_READY: 'No listo para entrega',
    PACKING: 'En proceso de empaque',
    PARTIALLY_READY: 'Parcialmente listo',
    READY: 'Listo para entrega',
    ON_ROUTE: 'En ruta de entrega',
    DELIVERED: 'Entregado al cliente',
    RETURNED: 'Devuelto',
    DEFAULT: 'Estado actualizado'
  };
  return messages[status] || messages.DEFAULT;
}

// Suscripción en tiempo real a cambios en la colección 'egresoCab'
export function subscribeToEgresoCabs(handleSearch, debounceMs = 800) {
  let refreshTimeout;

  const scheduleRefresh = () => {
    clearTimeout(refreshTimeout);
    refreshTimeout = setTimeout(() => {
      handleSearch();
    }, debounceMs);
  };

  // Resilient subscribe (reconnects after Maguen PB restarts / network drops)
  subscribeSafe('egresoCab', '*', async (e) => {
    if (e.action === 'update') {
      const currentRecord = e.record;
      const previousSta = previousValues.get(currentRecord.id)?.sta;

      if (previousSta !== currentRecord.sta) {
        const audio = new Audio('/assets/sounds/notification-1.wav');
        audio.play().catch(() => {});

        try {
          await pb.collection('notificacion').create(
            {
              mensaje: `Cambio de estado en egreso #${currentRecord.numero}: ${currentRecord.nombre} - ${getStatusMessage(currentRecord.sta)}`,
              sta: true,
              user_id: pb.authStore.model?.id || null,
            },
            { requestKey: null },
          );
        } catch (error) {
          // Ignore abort/autocancel leftovers
          if (!error?.isAbort && error?.status !== 0) {
            console.error('Error creating notification:', error);
          }
        }

        const status = currentRecord.sta || 'DEFAULT';
        showToastWithThrottle({
          title: 'Cambio de Estado #' + currentRecord.numero,
          description: currentRecord.nombre + ' - ' + getStatusMessage(status),
          duration: 4000,
          placement: 'bottom-right',
          type: statusToToastType[status],
          theme: 'dark',
          showProgress: true
        });
      }

      previousValues.set(currentRecord.id, { ...currentRecord });
    }

    scheduleRefresh();
  });

  // Mesa claim creates egresoPicker — refresh admin board even if sta event is filtered by rules
  subscribeSafe('egresoPicker', '*', () => {
    scheduleRefresh();
  });
}

// Desuscripción de los eventos en tiempo real
export function unsubscribeFromEgresoCabs() {
  removeSubscription('egresoCab', '*');
  removeSubscription('egresoPicker', '*');
}

/**
 * Realtime for "Mis pedidos de mesa":
 * - egresoCab: status → ASIGNACION_MESA (or leave it)
 * - egresoPicker: mesa/picker assigned or removed for this user
 */
export function subscribeToPickerEgresos(userId, handleRefresh, debounceMs = 300) {
  if (!userId) return;

  let refreshTimeout;
  const scheduleRefresh = () => {
    clearTimeout(refreshTimeout);
    refreshTimeout = setTimeout(() => {
      handleRefresh();
    }, debounceMs);
  };

  // Same pattern as admin: any egresoCab / egresoPicker change → reload board
  subscribeSafe('egresoCab', '*', () => {
    scheduleRefresh();
  });

  subscribeSafe('egresoPicker', '*', () => {
    scheduleRefresh();
  });
}

export function unsubscribeFromPickerEgresos() {
  removeSubscription('egresoCab', '*');
  removeSubscription('egresoPicker', '*');
}

// Buscar egresos por descripción con paginación
export async function searchEgresoCabs(query, page = 1, perPage = 10) {
  try {
    const response = await pb.collection('egresoCab').getList(page, perPage, {
      filter: `descrip ~ "${query}"`,
    });
    return {
      egresoCab: response.items,
      totalRecords: response.totalItems,
      currentPage: response.currentPage,
    };
  } catch (error) {
    console.error("Error searching egresoCabs:", error);
    Swal.fire('Error', 'No se pudo realizar la búsqueda', 'error');
    return null;
  }
}